/**
 * ProductCard — single reusable product tile used everywhere we list
 * products (home rails, store listing, search results, FBT, recommended,
 * "buy again", etc.).
 *
 * Three variants:
 *   • grid    — squared image-on-top tile (used in product grids)
 *   • compact — narrow rail tile (used in horizontal "Deals", "Trending")
 *   • wide    — full-width row with image on the left (used in cart upsell)
 *
 * Uses the design-system PriceTag for "₹400 ~~₹500~~ 20% OFF", a single
 * QuantityStepper for the ADD↔stepper morph, and SmartImage for safe
 * lazy/loading.
 *
 * NOTE: Does NOT know about cart APIs. Page-level code wires up the cart
 * via `onAdd / onIncrement / onDecrement` so this card can be re-used by
 * wishlist / restaurant menus / etc.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, PriceTag, RatingStars, VegDot, css } from '../../design';
import QuantityStepper from '../QuantityStepper';
import SmartImage from '../SmartImage';
import { Product } from '../../api/products';

export type ProductCardVariant = 'grid' | 'compact' | 'wide';

export interface ProductCardProps {
  product: Product;
  qty: number;
  variant?: ProductCardVariant;
  /** Loading state for the ADD button. */
  adding?: boolean;
  /** Highlight ring (used when search/focus deep-links to a product). */
  highlight?: boolean;
  /** Optional rating + review count (lazy-loaded by parent rail). */
  rating?: { value: number; count: number } | null;
  /** "Bestseller", "New", … pill rendered top-left over the image. */
  badge?: { label: string; variant?: 'accent' | 'primary' | 'food' | 'warning' } | null;
  /** Store name rendered beneath the price ("at FreshMart"). Used for global rails. */
  storeName?: string | null;
  /** Hide the rating block even if `rating` is provided. */
  hideRating?: boolean;
  onAdd: () => void | Promise<void>;
  onIncrement: () => void | Promise<void>;
  onDecrement: () => void | Promise<void>;
  /** Card click — defaults to `/product/:id` PDP. Override to "open store" etc. */
  onClick?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  qty,
  variant = 'grid',
  adding,
  highlight,
  rating,
  badge,
  storeName,
  hideRating,
  onAdd,
  onIncrement,
  onDecrement,
  onClick,
}) => {
  const navigate = useNavigate();

  // Resolve the effective price + MRP. Server may pre-compute these via
  // `effective_price` / `effective_discount_percent` (store-level discount
  // overlay). Fall back to MRP-based math otherwise.
  const basePrice = product.mrp && product.mrp > product.price ? product.mrp : product.price;
  const effectivePrice = product.effective_price != null
    ? product.effective_price
    : product.price;
  const effectiveDiscount =
    product.effective_discount_percent != null
      ? Math.round(product.effective_discount_percent)
      : basePrice > product.price
        ? Math.round(((basePrice - product.price) / basePrice) * 100)
        : 0;

  const handleClick = onClick ?? (() => navigate(`/product/${product.id}`));
  const inStock = product.is_available !== false && (product.stock_quantity ?? 1) > 0;

  if (variant === 'wide') {
    return (
      <div
        id={`product-card-${product.id}`}
        role="article"
        style={{
          display: 'flex',
          gap: 12,
          padding: 12,
          background: css.color.surface,
          border: `1px solid ${highlight ? css.color.primary : css.color.border}`,
          borderRadius: 14,
          boxShadow: highlight ? css.shadow.brand : css.shadow.sm,
          alignItems: 'center',
        }}
      >
        <div
          onClick={handleClick}
          style={{ width: 84, height: 84, flexShrink: 0, cursor: 'pointer', borderRadius: 10, overflow: 'hidden' }}
        >
          <SmartImage src={product.image_url} thumb={product.thumbnail_url} alt={product.name} size="thumb" radius={10} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <VegDot veg={product.is_veg} halal={product.is_halal} />
            {badge && <Badge variant={badge.variant ?? 'accent'} size="sm">{badge.label}</Badge>}
            {product.is_halal && <Badge variant="halal" size="sm">HALAL</Badge>}
          </div>
          <div
            onClick={handleClick}
            style={{ cursor: 'pointer', fontWeight: 700, fontSize: 14, color: css.color.text, lineHeight: 1.25 }}
          >
            {product.name}
          </div>
          {(product.weight || product.unit) && (
            <div style={{ fontSize: 12, color: css.color.textMuted, marginTop: 2 }}>
              {product.weight || `per ${product.unit}`}
            </div>
          )}
          {!hideRating && rating && (
            <div style={{ marginTop: 4 }}>
              <RatingStars rating={rating.value} count={rating.count} variant="compact" />
            </div>
          )}
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <PriceTag price={effectivePrice} mrp={basePrice} discountPercent={effectiveDiscount} size="sm" />
            <div style={{ width: 110 }}>
              <QuantityStepper
                value={qty}
                onChange={(next) => {
                  if (next > qty) onIncrement();
                  else if (next < qty) onDecrement();
                  else onAdd();
                }}
                min={0}
                max={Math.max(1, product.stock_quantity ?? 99)}
                disabled={!inStock}
                loading={!!adding}
              />
            </div>
          </div>
          {storeName && (
            <div style={{ fontSize: 11, color: css.color.textSubtle, marginTop: 4 }}>
              at <span style={{ color: css.color.text2, fontWeight: 600 }}>{storeName}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // grid + compact share the vertical layout, with `compact` being slimmer
  const compact = variant === 'compact';

  return (
    <div
      id={`product-card-${product.id}`}
      role="article"
      style={{
        position: 'relative',
        background: css.color.surface,
        border: `1px solid ${highlight ? css.color.primary : css.color.border}`,
        borderRadius: 14,
        boxShadow: highlight ? css.shadow.brand : css.shadow.sm,
        padding: compact ? 10 : 12,
        cursor: 'pointer',
        transition: 'box-shadow 200ms ease, transform 200ms ease, border-color 200ms ease',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = css.shadow.cardHover;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = css.color.primary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = highlight ? css.shadow.brand : css.shadow.sm;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = highlight ? css.color.primary : css.color.border;
      }}
      onClick={(e) => {
        // Don't navigate when interacting with ADD / stepper.
        const tag = (e.target as HTMLElement)?.closest('button');
        if (tag) return;
        handleClick();
      }}
    >
      {/* Badges (top-left) */}
      <div style={{ position: 'absolute', top: 8, left: 8, display: 'inline-flex', flexDirection: 'column', gap: 4, zIndex: 1 }}>
        {effectiveDiscount > 0 && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: '#FFFFFF',
              background: `linear-gradient(135deg, ${css.color.accent}, ${css.color.accentStrong})`,
              padding: '3px 6px',
              borderRadius: 4,
              letterSpacing: '0.02em',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            }}
          >
            {effectiveDiscount}% OFF
          </span>
        )}
        {badge && (
          <Badge variant={badge.variant ?? 'accent'} size="sm" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            {badge.label}
          </Badge>
        )}
      </div>

      {/* Veg/Non-veg (top-right) */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
        <VegDot veg={product.is_veg} halal={product.is_halal} />
        {product.is_halal && <Badge variant="halal" size="sm">HALAL</Badge>}
      </div>

      {/* Image */}
      <div
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          background: css.color.surface2,
          borderRadius: 10,
          overflow: 'hidden',
          marginBottom: 10,
        }}
      >
        <SmartImage src={product.image_url} thumb={product.thumbnail_url} alt={product.name} size="thumb" radius={10} />
      </div>

      {/* Out of stock overlay */}
      {!inStock && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 14,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              background: css.color.text,
              color: '#FFFFFF',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Out of stock
          </span>
        </div>
      )}

      {/* Weight / unit */}
      {(product.weight || product.unit) && (
        <div style={{ fontSize: 11, color: css.color.textMuted, marginBottom: 2 }}>
          {product.weight || (product.unit && product.unit !== 'piece' ? `per ${product.unit}` : '')}
        </div>
      )}

      {/* Name */}
      <div
        style={{
          fontSize: compact ? 13 : 13,
          fontWeight: 700,
          color: css.color.text,
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '2.6em',
        }}
      >
        {product.name}
      </div>

      {/* Rating */}
      {!hideRating && rating && rating.value > 0 && (
        <div style={{ marginTop: 4 }}>
          <RatingStars rating={rating.value} count={rating.count} variant="compact" size={12} />
        </div>
      )}

      {/* Store attribution (only in global rails) */}
      {storeName && (
        <div style={{ fontSize: 11, color: css.color.textSubtle, marginTop: 2 }}>
          at <span style={{ color: css.color.text2, fontWeight: 600 }}>{storeName}</span>
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Price + ADD */}
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <PriceTag price={effectivePrice} mrp={basePrice} discountPercent={effectiveDiscount} size="sm" stacked />
        <div style={{ width: compact ? 80 : 86 }}>
          <QuantityStepper
            value={qty}
            onChange={(next) => {
              if (next > qty) onIncrement();
              else if (next < qty) onDecrement();
              else onAdd();
            }}
            min={0}
            max={Math.max(1, product.stock_quantity ?? 99)}
            disabled={!inStock}
            loading={!!adding}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCard;

